import { useRef, useEffect, useState } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useJobData } from '@/shared/hooks/useJobData';
import type { JobData } from '@/lib/types/Job';

export interface PfileImageJobStatus {
  phase: 'ingesting' | 'processing' | 'complete' | 'failed';
  progress: number; // 0-100
}

/**
 * Consumer of the shared useJobData hook for pfile image processing.
 *
 * When ingestJobId is known (locally initiated upload), matches exact job by ID.
 * When ingestJobId is null (resume after reload), matches any active pfile image
 * job with stale deduplication to avoid reacting to old jobs.
 */
export function usePfileImageProcessing(
  isProcessing: boolean,
  ingestJobId: number | null
): PfileImageJobStatus | null {
  const { selectedCId } = useAuth();
  const { data: jobData, dataUpdatedAt } = useJobData(selectedCId, isProcessing);
  const [status, setStatus] = useState<PfileImageJobStatus | null>(null);

  // Stale job dedup: snapshot completed job IDs on first poll (resume cycles only)
  const staleJobIds = useRef<Set<number>>(new Set());
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!jobData || !isProcessing) {
      if (!isProcessing) setStatus(null);
      return;
    }

    const { ingestPfileImageJobs, transPfileImageJobs } = jobData;

    // Initialize stale job set on first poll (only needed for resume cycles)
    if (!initializedRef.current) {
      initializedRef.current = true;
      if (ingestJobId == null) {
        for (const job of [...ingestPfileImageJobs, ...transPfileImageJobs]) {
          if (job.jobStatus === 'completed' || job.jobStatus === 'failed' || job.jobStatus === 'canceled') {
            staleJobIds.current.add(job.jobId);
          }
        }
      }
    }

    let ingestJob: JobData | undefined;
    let transJob: JobData | undefined;

    if (ingestJobId != null) {
      // Precise match by job ID (locally initiated)
      ingestJob = ingestPfileImageJobs.find(j => j.jobId === ingestJobId);
      // Transcode job follows ingest — match any non-stale pfile trans job
      transJob = transPfileImageJobs.find(
        j => !staleJobIds.current.has(j.jobId)
      );
    } else {
      // Resume cycle: match any non-stale pfile image job
      ingestJob = ingestPfileImageJobs.find(
        j => !staleJobIds.current.has(j.jobId)
      );
      transJob = transPfileImageJobs.find(
        j => !staleJobIds.current.has(j.jobId)
      );
    }

    // Determine status (transcode takes priority since it comes after ingest)
    if (transJob) {
      if (transJob.jobStatus === 'completed') {
        setStatus({ phase: 'complete', progress: 100 });
      } else if (transJob.jobStatus === 'failed' || transJob.jobStatus === 'canceled') {
        setStatus({ phase: 'failed', progress: 0 });
      } else {
        setStatus({ phase: 'processing', progress: transJob.completedRate });
      }
    } else if (ingestJob) {
      if (ingestJob.jobStatus === 'completed') {
        // Ingest done but transcode hasn't appeared yet
        setStatus({ phase: 'processing', progress: 0 });
      } else if (ingestJob.jobStatus === 'failed' || ingestJob.jobStatus === 'canceled') {
        setStatus({ phase: 'failed', progress: 0 });
      } else {
        setStatus({ phase: 'ingesting', progress: ingestJob.completedRate });
      }
    }
    // If no matching jobs found, status stays as previous (job may not have appeared yet)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt, isProcessing, ingestJobId]);

  // Reset initialization when processing restarts (new upload cycle)
  useEffect(() => {
    if (!isProcessing) {
      initializedRef.current = false;
      staleJobIds.current.clear();
    }
  }, [isProcessing]);

  return status;
}
